import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../utils/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { CheckCircle, XCircle, Download, Loader } from 'lucide-react';

const VerifyExternal = () => {
  const { code } = useParams();
  const [cert, setCert] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const verify = async () => {
      try {
        const res = await api.get(`/certificates/external/verify/${code}`);
        setCert(res.data);
      } catch {
        setError(true);
      } finally {
        setLoading(false);
      }
    };
    if (code) verify();
  }, [code]);

  const downloadUrl = cert
    ? `${process.env.REACT_APP_BACKEND_URL}/api/certificates/external/${cert.id}/download`
    : null;

  return (
    <div className="min-h-screen bg-secondary flex items-center justify-center p-6">
      <div className="max-w-lg w-full">
        {loading && (
          <Card className="text-center">
            <CardContent className="py-12">
              <Loader className="w-10 h-10 animate-spin mx-auto text-primary mb-3" />
              <p className="text-muted-foreground">Verificando certificado...</p>
            </CardContent>
          </Card>
        )}

        {!loading && error && (
          <Card data-testid="external-verify-invalid">
            <CardContent className="py-10 text-center space-y-4">
              <div className="w-20 h-20 rounded-full bg-destructive/10 flex items-center justify-center mx-auto">
                <XCircle className="w-12 h-12 text-destructive" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-destructive">Certificado no válido</h2>
                <p className="text-muted-foreground mt-1">
                  No se encontró ningún certificado con el código <span className="font-mono font-bold">{code}</span>.
                </p>
              </div>
              <Link to="/">
                <Button variant="outline">Ir al inicio</Button>
              </Link>
            </CardContent>
          </Card>
        )}

        {!loading && cert && (
          <Card className="shadow-xl" data-testid="external-verify-valid">
            <CardHeader className="text-center pb-2">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-3">
                <CheckCircle className="w-12 h-12 text-green-600" />
              </div>
              <Badge className="bg-green-600 text-white mx-auto w-fit">Certificado Válido</Badge>
            </CardHeader>
            <CardContent className="space-y-5">
              <div className="bg-muted rounded-lg p-5 space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Titular</p>
                  <p className="text-lg font-bold">{cert.owner_name}</p>
                </div>
                {cert.course_name && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Curso / Programa</p>
                    <p className="font-semibold">{cert.course_name}</p>
                  </div>
                )}
                {cert.issue_date && (
                  <div>
                    <p className="text-xs text-muted-foreground uppercase tracking-wide">Fecha de Emisión</p>
                    <p className="font-semibold">{new Date(cert.issue_date).toLocaleDateString('es-CO', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                )}
                <div>
                  <p className="text-xs text-muted-foreground uppercase tracking-wide">Código de Verificación</p>
                  <p className="font-mono font-bold text-primary tracking-wider">{cert.code}</p>
                </div>
              </div>

              <div className="flex flex-col gap-2">
                {downloadUrl && (
                  <a href={downloadUrl} target="_blank" rel="noopener noreferrer" data-testid="download-verified-btn">
                    <Button className="w-full">
                      <Download className="w-4 h-4 mr-2" />
                      Descargar Certificado
                    </Button>
                  </a>
                )}
                <Link to="/">
                  <Button variant="outline" className="w-full">Ir al inicio</Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default VerifyExternal;
